import { useState } from 'react';
import { Field, TextInput } from './ui/Field';
import { SegmentedControl } from './ui/SegmentedControl';
import { useLocalDay } from '../hooks/useLocalDay';
import {
  bannerText, bookingSchedule, bookingWindowIsBackwards, EMPTY_BOOKING, todayKey,
  type BookingForm, type TimeMode,
} from '../lib/schedule';

const TIME_OPTIONS: ReadonlyArray<{ value: TimeMode; label: string }> = [
  { value: 'anyTime', label: 'Any time' },
  { value: 'at', label: 'At' },
  { value: 'between', label: 'Between' },
];

/**
 * The booking in a form (PR-43, v7 design Bobby approved on 1 Nov 2026): a Date section
 * ("Not set" until a day is picked) and a Time section (Any time, At, Between). A time
 * picked with no date makes the date today and says so; Clear date clears both. Used by
 * New Job and the job page; the parent sends `bookingFields(value)`.
 */
export function BookingFields({
  value,
  onChange,
  idPrefix,
}: {
  value: BookingForm;
  onChange: (next: BookingForm) => void;
  idPrefix: string;
}) {
  const [dateSetByTime, setDateSetByTime] = useState(false);
  const now = new Date(useLocalDay());
  const shows = bannerText(bookingSchedule(value), now);
  const backwards = bookingWindowIsBackwards(value);

  const setTime = (key: 'at' | 'from' | 'to', time: string) => {
    if (!value.date && time) {
      setDateSetByTime(true);
      onChange({ ...value, [key]: time, date: todayKey() });
    } else {
      onChange({ ...value, [key]: time });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-ink3">Date</h3>
        <Field label="Date" htmlFor={`${idPrefix}-date`}>
          <TextInput
            id={`${idPrefix}-date`}
            type="date"
            value={value.date}
            onChange={(e) => {
              setDateSetByTime(false);
              onChange(e.target.value ? { ...value, date: e.target.value } : EMPTY_BOOKING);
            }}
          />
        </Field>
        <p className="text-[13px] text-ink3">
          {!value.date
            ? 'Not set shows as ASAP on Live Jobs.'
            : dateSetByTime
              ? 'Date set to today because a time was picked.'
              : null}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-ink3">Time</h3>
        <SegmentedControl label="Time" options={TIME_OPTIONS} value={value.mode} onChange={(mode) => onChange({ ...value, mode })} />
        {value.mode === 'at' && (
          <Field label="At" htmlFor={`${idPrefix}-at`}>
            <TextInput id={`${idPrefix}-at`} type="time" value={value.at} onChange={(e) => setTime('at', e.target.value)} />
          </Field>
        )}
        {value.mode === 'between' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="From" htmlFor={`${idPrefix}-from`}>
              <TextInput id={`${idPrefix}-from`} type="time" value={value.from} onChange={(e) => setTime('from', e.target.value)} />
            </Field>
            <Field label="To" htmlFor={`${idPrefix}-to`}>
              <TextInput id={`${idPrefix}-to`} type="time" value={value.to} onChange={(e) => setTime('to', e.target.value)} />
            </Field>
          </div>
        )}
        {backwards && <p className="text-[13px] text-st-cancelled">The end of the window must be after its start.</p>}
        {value.date && shows && !backwards && (
          <p className="text-[14px] text-ink2">
            <span className="text-ink3">Shows as </span>
            <span className="font-semibold text-ink">{shows}</span>
          </p>
        )}
        {value.date && (
          <button
            type="button"
            className="self-start text-[14px] font-medium text-st-cancelled hover:underline"
            onClick={() => {
              setDateSetByTime(false);
              onChange(EMPTY_BOOKING);
            }}
          >
            Clear date
          </button>
        )}
      </div>
    </div>
  );
}
